import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, limit, serverTimestamp, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logError, log } from '../utils/logger';
import { useAuth } from './AuthContext';

export interface Contact {
  id: string;
  name: string;
  nickname?: string;
  age?: number;
  photoURL?: string;
  userId?: string; // For QR scanning
  phoneNumber?: string; // For contact identification
  notes?: string;
  tags?: string[];
  lastActivity?: Date;
  totalActivities?: number;
  avgRating?: number;
  created: Date;
}

export interface HookupLog {
  id: string;
  date: Date;
  partners: string[]; // Contact IDs
  partnerContacts?: Contact[]; // For display purposes
  location: string;
  activities: string[];
  rating: number;
  notes?: string;
  duration?: number; // in minutes
  protection?: boolean;
}

export interface ActivityStats {
  totalHookups: number;
  uniquePartners: number;
  avgRating: number;
  favoriteActivity: string;
  favoriteLocation: string;
  thisMonth: number;
  thisWeek: number;
}

export interface ActivityContextType {
  contacts: Contact[];
  hookupLogs: HookupLog[];
  stats: ActivityStats;
  loading: boolean;
  addContact: (contact: Omit<Contact, 'id' | 'created'>) => Promise<string | null>;
  updateContact: (contactId: string, updates: Partial<Contact>) => Promise<void>;
  deleteContact: (contactId: string) => Promise<void>;
  addHookupLog: (log: Omit<HookupLog, 'id'>) => Promise<string | null>;
  updateHookupLog: (logId: string, updates: Partial<HookupLog>) => Promise<void>;
  deleteHookupLog: (logId: string) => Promise<void>;
  getContactNames: (contactIds: string[]) => string[];
  getContactObjects: (contactIds: string[]) => Contact[];
  findExistingContact: (name: string, phone?: string, userId?: string) => Contact | null;
}

const ActivityContext = createContext<ActivityContextType | undefined>(undefined);

export const useActivity = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivity must be used within an ActivityProvider');
  }
  return context;
};

interface ActivityProviderProps {
  children: ReactNode;
}

export const ActivityProvider: React.FC<ActivityProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const currentUserId = user?.uid || '';
  
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [hookupLogs, setHookupLogs] = useState<HookupLog[]>([]);
  const [loading, setLoading] = useState(true);

  // Firebase real-time listeners for contacts
  useEffect(() => {
    if (!currentUserId) return;

    const contactsRef = collection(db, 'users', currentUserId, 'contacts');
    const contactsQuery = query(contactsRef, orderBy('created', 'desc'), limit(100));

    const unsubscribe = onSnapshot(contactsQuery, (snapshot) => {
      const userContacts: Contact[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        userContacts.push({
          id: doc.id,
          name: data.name,
          nickname: data.nickname,
          age: data.age,
          photoURL: data.photoURL,
          userId: data.userId,
          phoneNumber: data.phoneNumber,
          notes: data.notes,
          tags: data.tags || [],
          lastActivity: data.lastActivity?.toDate(),
          totalActivities: data.totalActivities || 0,
          avgRating: data.avgRating || 0,
          created: data.created?.toDate() || new Date()
        });
      });
      setContacts(userContacts);
      setLoading(false);
    }, (error) => {
      logError('Error fetching contacts:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  // Firebase real-time listeners for hookup logs
  useEffect(() => {
    if (!currentUserId) return;

    const logsRef = collection(db, 'users', currentUserId, 'activityLogs');
    const logsQuery = query(logsRef, orderBy('date', 'desc'), limit(100));

    const unsubscribe = onSnapshot(logsQuery, (snapshot) => {
      const logs: HookupLog[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          date: data.date?.toDate() || new Date(),
          partners: data.partners || [],
          location: data.location,
          activities: data.activities || [],
          rating: data.rating || 0,
          notes: data.notes,
          duration: data.duration,
          protection: data.protection
        });
      });
      setHookupLogs(logs);
    }, (error) => {
      logError('Error fetching activity logs:', error);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  // Calculate stats based on current data
  const stats: ActivityStats = {
    totalHookups: hookupLogs.length,
    uniquePartners: [...new Set(hookupLogs.flatMap(log => log.partners))].length,
    avgRating: hookupLogs.length > 0 ? hookupLogs.reduce((acc, log) => acc + log.rating, 0) / hookupLogs.length : 0,
    favoriteActivity: getFavoriteActivity(),
    favoriteLocation: getFavoriteLocation(),
    thisMonth: hookupLogs.filter(log => log.date.getMonth() === new Date().getMonth()).length,
    thisWeek: hookupLogs.filter(log => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return log.date >= weekAgo;
    }).length
  };

  function getFavoriteActivity(): string {
    if (hookupLogs.length === 0) return 'None';
    const activityCount = hookupLogs.reduce((acc, log) => {
      log.activities.forEach(activity => {
        acc[activity] = (acc[activity] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>);
    
    const [favorite] = Object.entries(activityCount).sort(([,a], [,b]) => b - a)[0] || ['None', 0];
    return favorite;
  }

  function getFavoriteLocation(): string {
    if (hookupLogs.length === 0) return 'None';
    const locationCount = hookupLogs.reduce((acc, log) => {
      acc[log.location] = (acc[log.location] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const [favorite] = Object.entries(locationCount).sort(([,a], [,b]) => b - a)[0] || ['None', 0];
    return favorite;
  }

  // Helper functions
  const getContactNames = (contactIds: string[]): string[] => {
    return contactIds.map(id => {
      const contact = contacts.find(c => c.id === id);
      return contact?.nickname || contact?.name || 'Unknown';
    });
  };

  const getContactObjects = (contactIds: string[]): Contact[] => {
    return contactIds.map(id => contacts.find(c => c.id === id)).filter(Boolean) as Contact[];
  };

  const findExistingContact = (name: string, phone?: string, userId?: string): Contact | null => {
    return contacts.find(contact => 
      contact.name.toLowerCase() === name.toLowerCase() ||
      (phone && contact.phoneNumber === phone) ||
      (userId && contact.userId === userId)
    ) || null;
  };

  // CRUD operations
  const addContact = async (contactData: Omit<Contact, 'id' | 'created'>): Promise<string | null> => {
    try {
      const docRef = await addDoc(collection(db, 'users', currentUserId, 'contacts'), {
        ...contactData,
        created: serverTimestamp(),
        lastActivity: contactData.lastActivity || null,
        totalActivities: contactData.totalActivities || 0,
        avgRating: contactData.avgRating || 0
      });
      log('Added new contact:', docRef.id);
      return docRef.id;
    } catch (error) {
      logError('Error adding contact:', error);
      return null;
    }
  };

  const updateContact = async (contactId: string, updates: Partial<Contact>): Promise<void> => {
    try {
      await updateDoc(doc(db, 'users', currentUserId, 'contacts', contactId), {
        ...updates,
        lastActivity: updates.lastActivity || null
      });
      log('Updated contact:', contactId);
    } catch (error) {
      logError('Error updating contact:', error);
    }
  };

  const deleteContact = async (contactId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'users', currentUserId, 'contacts', contactId));
      log('Deleted contact:', contactId);
    } catch (error) {
      logError('Error deleting contact:', error);
    }
  };

  const addHookupLog = async (logData: Omit<HookupLog, 'id'>): Promise<string | null> => {
    try {
      const docRef = await addDoc(collection(db, 'users', currentUserId, 'activityLogs'), {
        ...logData,
        date: logData.date || serverTimestamp()
      });
      
      // Update contact stats
      for (const partnerId of logData.partners) {
        const contact = contacts.find(c => c.id === partnerId);
        if (contact) {
          await updateContact(partnerId, {
            lastActivity: logData.date || new Date(),
            totalActivities: (contact.totalActivities || 0) + 1,
            avgRating: contact.avgRating // TODO: Calculate new average with this rating
          });
        }
      }
      
      log('Added new activity log:', docRef.id);
      return docRef.id;
    } catch (error) {
      logError('Error adding activity log:', error);
      return null;
    }
  };

  const updateHookupLog = async (logId: string, updates: Partial<HookupLog>): Promise<void> => {
    try {
      await updateDoc(doc(db, 'users', currentUserId, 'activityLogs', logId), {
        ...updates,
        date: updates.date || null
      });
      log('Updated activity log:', logId);
    } catch (error) {
      logError('Error updating activity log:', error);
    }
  };

  const deleteHookupLog = async (logId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'users', currentUserId, 'activityLogs', logId));
      log('Deleted activity log:', logId);
    } catch (error) {
      logError('Error deleting activity log:', error);
    }
  };

  const value: ActivityContextType = {
    contacts,
    hookupLogs,
    stats,
    loading,
    addContact,
    updateContact,
    deleteContact,
    addHookupLog,
    updateHookupLog,
    deleteHookupLog,
    getContactNames,
    getContactObjects,
    findExistingContact
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};